/**
 * Social Media API Module
 * Handles all social media management API calls
 */

import { BASE_URL, fetchWithAuth, getAuthHeaders } from './client';

// ============================================================================
// Platform Management
// ============================================================================

/**
 * Get list of supported social media platforms
 * @returns {Promise<{ data: Array }>}
 */
export const getPlatforms = async () => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/platforms`);
    if (!response.ok) {
        throw new Error('Failed to fetch platforms');
    }
    return response.json();
};

/**
 * Get OAuth URL for a platform
 * @param {string} platform - Platform ID (youtube, facebook, instagram, linkedin)
 * @returns {Promise<{ url: string, state: string }>}
 */
export const getOAuthUrl = async (platform) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/platforms/${platform}/oauth-url`);
    if (!response.ok) {
        throw new Error(`Failed to get OAuth URL for ${platform}`);
    }
    return response.json();
};

/**
 * Complete OAuth flow with authorization code
 * @param {string} platform - Platform ID
 * @param {string} code - Authorization code from OAuth callback
 * @param {string} state - State parameter for CSRF verification
 * @returns {Promise<{ data: Object }>}
 */
export const completeOAuth = async (platform, code, state) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/platforms/${platform}/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, state }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || `Failed to complete OAuth for ${platform}`);
    }
    return response.json();
};

// ============================================================================
// Integration Management
// ============================================================================

/**
 * Get all connected integrations
 * @param {string} [platform] - Optional platform filter
 * @returns {Promise<{ data: Array }>}
 */
export const getIntegrations = async (platform = null) => {
    const params = platform ? `?platform=${platform}` : '';
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/integrations${params}`);
    if (!response.ok) {
        throw new Error('Failed to fetch integrations');
    }
    return response.json();
};

/**
 * Get a single integration by ID
 * @param {string} id - Integration ID
 * @returns {Promise<{ data: Object }>}
 */
export const getIntegration = async (id) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/integrations/${id}`);
    if (!response.ok) {
        throw new Error('Failed to fetch integration');
    }
    return response.json();
};

/**
 * Delete/disconnect an integration
 * @param {string} id - Integration ID
 * @returns {Promise<{ success: boolean }>}
 */
export const deleteIntegration = async (id) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/integrations/${id}`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        throw new Error('Failed to disconnect integration');
    }
    return response.json();
};

/**
 * Refresh an integration's access token
 * @param {string} id - Integration ID
 * @returns {Promise<{ data: Object }>}
 */
export const refreshIntegration = async (id) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/integrations/${id}/refresh`, {
        method: 'POST',
    });
    if (!response.ok) {
        throw new Error('Failed to refresh integration');
    }
    return response.json();
};

// ============================================================================
// Post Management
// ============================================================================

/**
 * Create a new post for a single platform
 * @param {Object} data - Post data
 * @param {string} data.integration_id - Target integration ID
 * @param {string} data.content - Post content
 * @param {string[]} [data.media_urls] - Optional media URLs
 * @param {string} [data.scheduled_at] - Optional ISO date for scheduling
 * @param {Object} [data.platform_settings] - Optional platform-specific settings
 * @returns {Promise<{ data: Object }>}
 */
export const createPost = async (data) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to create post');
    }
    return response.json();
};

/**
 * Create a cross-post for multiple platforms
 * @param {Object} data - Cross-post data
 * @param {string[]} data.integration_ids - Target integration IDs
 * @param {string} data.content - Post content
 * @param {string[]} [data.media_urls] - Optional media URLs
 * @param {string} [data.scheduled_at] - Optional ISO date for scheduling
 * @param {Object} [data.platform_settings] - Optional per-platform settings
 * @returns {Promise<{ data: Array }>}
 */
export const createCrossPost = async (data) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/posts/cross-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to create cross-post');
    }
    return response.json();
};

/**
 * Get posts with optional filters
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.status] - Filter by status (draft, scheduled, published, failed)
 * @param {string} [filters.integration_id] - Filter by integration
 * @param {string} [filters.start_date] - Filter posts after this date
 * @param {string} [filters.end_date] - Filter posts before this date
 * @param {number} [filters.limit] - Number of posts to return (default 20)
 * @param {string} [filters.cursor] - Pagination cursor
 * @returns {Promise<{ data: Array, cursor: string|null, has_more: boolean }>}
 */
export const getPosts = async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.integration_id) params.append('integration_id', filters.integration_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.cursor) params.append('cursor', filters.cursor);

    const queryString = params.toString();
    const url = `${BASE_URL}/api/social-media/posts${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);
    if (!response.ok) {
        throw new Error('Failed to fetch posts');
    }
    return response.json();
};

/**
 * Get a single post by ID
 * @param {string} id - Post ID
 * @returns {Promise<{ data: Object }>}
 */
export const getPost = async (id) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/posts/${id}`);
    if (!response.ok) {
        throw new Error('Failed to fetch post');
    }
    return response.json();
};

/**
 * Update an existing post
 * @param {string} id - Post ID
 * @param {Object} data - Updated post data
 * @returns {Promise<{ data: Object }>}
 */
export const updatePost = async (id, data) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/posts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to update post');
    }
    return response.json();
};

/**
 * Delete a post
 * @param {string} id - Post ID
 * @returns {Promise<{ success: boolean }>}
 */
export const deletePost = async (id) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/posts/${id}`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        throw new Error('Failed to delete post');
    }
    return response.json();
};

/**
 * Publish a draft or scheduled post immediately
 * @param {string} id - Post ID
 * @returns {Promise<{ data: Object }>}
 */
export const publishPost = async (id) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/posts/${id}/publish`, {
        method: 'POST',
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to publish post');
    }
    return response.json();
};

// ============================================================================
// Analytics
// ============================================================================

/**
 * Get analytics for an integration
 * @param {string} integrationId - Integration ID
 * @param {string} [postId] - Optional specific post ID for post-level analytics
 * @param {Object} [options] - Optional parameters
 * @param {number} [options.days] - Number of days to fetch (default 30)
 * @returns {Promise<{ data: Object }>}
 */
export const getAnalytics = async (integrationId, postId = null, options = {}) => {
    const params = new URLSearchParams();
    if (postId) params.append('post_id', postId);
    if (options.days) params.append('days', options.days.toString());

    const queryString = params.toString();
    const url = `${BASE_URL}/api/social-media/analytics/${integrationId}${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);
    if (!response.ok) {
        throw new Error('Failed to fetch analytics');
    }
    return response.json();
};

// ============================================================================
// Media Upload
// ============================================================================

/**
 * Upload media for social media posts
 * @param {File} file - File to upload
 * @param {Function} [onProgress] - Optional progress callback
 * @returns {Promise<{ url: string, type: string }>}
 */
export const uploadMedia = async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${BASE_URL}/api/social-media/media/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to upload media');
    }

    return response.json();
};

/**
 * Delete uploaded media
 * @param {string} mediaUrl - URL of media to delete
 * @returns {Promise<{ success: boolean }>}
 */
export const deleteMedia = async (mediaUrl) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/social-media/media`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: mediaUrl }),
    });

    if (!response.ok) {
        throw new Error('Failed to delete media');
    }

    return response.json();
};
